import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import {IconButton} from "@mui/material";
import { useAuth } from "./TokenHandler";

import * as XLSX from "xlsx"; // Import xlsx library


const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    bgcolor: "#00A7A0",
    color: "white",
    padding: "8px 24px",
    flexShrink: 0, // Prevents it from shrinking
};

const OptOutReportModal = ({ open, onClose }) => {
    const { keycloak: kc } = useAuth();
    const [optOutData, setOptOutData] = useState([]);
    const [selectedCorporations, setSelectedCorporations] = useState([]);
    const [selectedFacilities, setSelectedFacilities] = useState([]);
    const [selectedStates, setSelectedStates] = useState([]);
    const [searchCorp, setSearchCorp] = useState("");
    const [searchFacility, setSearchFacility] = useState("");
    const [searchState, setSearchState] = useState("");

    const uniqueCorporations = [...new Set(optOutData.map(item => item.corp_name))];
    const uniqueFacilities = selectedCorporations.length > 0
        ? [...new Set(optOutData.filter(item => selectedCorporations.includes(item.corp_name)).map(item => item.facility_name))]
        : [...new Set(optOutData.map(item => item.facility_name))];

    const uniqueStates = selectedCorporations.length > 0
        ? [...new Set(optOutData.filter(item => selectedCorporations.includes(item.corp_name)).map(item => item.state))]
        : [...new Set(optOutData.map(item => item.state))];



    const [searchPatient, setSearchPatient] = useState("");

    const [loading, setLoading] = useState(false); // New state for loading indicator
    const [filtering, setFiltering] = useState(false); // Track filtering state
    const isDateColumn = (key) =>
        key === "dob" || key === "modifiedon" || key.toLowerCase().includes("date");

    // Which program fields should be conditionally shown
    const PROGRAM_KEYS = new Set(["ccm", "bhi", "rpm", "rtm"]);

// If source_system !== "Tapestry Opt Out Application", use calculated* fields.
// Otherwise use the raw ccm/bhi/rpm/rtm values.
    const getProgramValue = (row, key) => {
        if (!PROGRAM_KEYS.has(key)) return row?.[key];
        const useRaw = row?.source_system === "Tapestry Opt Out Application";
        const altKey = `calculated${key}`; // e.g., calculatedccm, calculatedbhi, ...
        return useRaw ? row?.[key] : row?.[altKey];
    };


    useEffect(() => {
        if (selectedCorporations.length > 0 || selectedFacilities.length > 0 || selectedStates.length > 0) {
            setFiltering(true);
            const timer = setTimeout(() => {
                setFiltering(false); // Stop filtering after a small delay
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [selectedCorporations, selectedFacilities, selectedStates]); // Run when a filter changes

    const [showColumnDropdown, setShowColumnDropdown] = useState(false);

    const [selectedColumns, setSelectedColumns] = useState([
        "corp_name", "facility_name", "state", "mrn", "modifiedon",
        "first_name", "last_name", "dob","status", "ccm", "ccm_reason", "rpm",
        "rpm_reason", "rtm", "rtm_reason"
    ]);

    const columnOrder = [
        "corp_name", "facility_name","source_id","mrn","state", "first_name", "last_name",
        "dob","status","modifiedon", "ccm", "ccm_reason","ccm_date" ,"rpm", "rpm_reason","rpm_date" ,
        "bhi", "bhi_reason", "bhi_date","notes_free_text","reason","source_system"
    ]; // Ensure this matches actual data keys

    const columnOptions = optOutData.length > 0 ? Object.keys(optOutData[0]) : [];


    const excludedColumns = ["ouid", "fac_id","patient_id","calculatedccm", "calculatedbhi", "calculatedrpm", "calculatedrtm"]; // Add column names you want to exclude
    const columnRenameMap = {
        source_id: "Provider Number",
        'reason': "Legacy Reason",
        'rpm': "RPMI",
        'rpm_reason': "RPMI reason",
        'rpm_date': "RPMI date",
        'bhi': "BHI",
        'bhi_reason': "BHI reason",
        'bhi_date': "BHI date",
        'notes_free_text': "Notes Free Text",
        'modifiedon': "Latest record date",
    }; // Mapping for renamed columns



    useEffect(() => {
        if (open) {
            setLoading(true);
            fetch(`${process.env.REACT_APP_API_URL}/get_opt_out_report`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error("Failed to fetch cleaned opt-outs from API");
                    }
                    return response.json();
                })
                .then((data) => {
                    if (data && data.cleaned_optouts) {
                        setOptOutData(data.cleaned_optouts);
                    } else {
                        console.error("Invalid JSON format: Missing 'cleaned_optouts' key");
                        setOptOutData([]);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching opt-out data:", error);
                    setOptOutData([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            // Clear all inputs and filters when modal closes
            setOptOutData([]);
            setSelectedCorporations([]);
            setSelectedFacilities([]);
            setSelectedStates([]);
            setSearchCorp("");
            setSearchFacility("");
            setSearchState("");
            setSearchPatient("");
            setSelectedColumns([
                "corp_name", "facility_name", "state", "mrn", "modifiedon",
                "first_name", "last_name", "dob", "ccm", "ccm_reason", "rpm",
                "rpm_reason", "rtm", "rtm_reason","status"
            ]);
        }
    }, [open]); // Only run when modal opens





    // Helper function to format dates to MM-DD-YYYY
// Helper: format many date shapes to MM/DD/YYYY
    const formatDate = (val) => {
        if (!val) return "";

        // If we get a string, try easy extractions first
        if (typeof val === "string") {
            const s = val.trim();

            // If it's already MM/DD/YYYY, keep it
            if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return s;

            // Handle ISO or space-separated timestamps by taking the date part
            const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];

            // YYYY-MM-DD → MM/DD/YYYY
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
            if (m) return `${m[2]}/${m[3]}/${m[1]}`;
        }

        // Fallback: let Date parse it (covers numeric ms since epoch & many strings)
        const d = new Date(val);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
    };


    // Filtering logic
    const filteredData = optOutData.filter(item => {
        const matchesCorp = selectedCorporations.length === 0 || selectedCorporations.includes(item.corp_name);
        const matchesFacility = selectedFacilities.length === 0 || selectedFacilities.includes(item.facility_name);
        const matchesState = selectedStates.length === 0 || selectedStates.includes(item.state);
        const matchesPatient = searchPatient === "" || (`${item.first_name} ${item.last_name}`).toLowerCase().includes(searchPatient.toLowerCase());

        return matchesCorp && matchesFacility && matchesState && matchesPatient;
    });

    // Enable patient search only if any filter is selected
    const isPatientSearchEnabled = selectedCorporations.length > 0 || selectedFacilities.length > 0 || selectedStates.length > 0;

    const handleCheckboxChange = (filterType, value) => {
        if (filterType === "corporation") {
            setSelectedCorporations((prev) => {
                const updatedCorporations = prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value];

                // Reset facility and state filters when a corporation is selected/deselected
                const filteredFacilities = updatedCorporations.length > 0
                    ? [...new Set(optOutData.filter(item => updatedCorporations.includes(item.corp_name)).map(item => item.facility_name))]
                    : [...new Set(optOutData.map(item => item.facility_name))];

                const filteredStates = updatedCorporations.length > 0
                    ? [...new Set(optOutData.filter(item => updatedCorporations.includes(item.corp_name)).map(item => item.state))]
                    : [...new Set(optOutData.map(item => item.state))];

                setSelectedFacilities(prev => prev.filter(fac => filteredFacilities.includes(fac)));
                setSelectedStates(prev => prev.filter(state => filteredStates.includes(state)));

                return updatedCorporations;
            });
        } else if (filterType === "facility") {
            setSelectedFacilities((prev) =>
                prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
            );
        } else if (filterType === "state") {
            setSelectedStates((prev) =>
                prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
            );
        }

        setFiltering(true);
        setTimeout(() => {
            setFiltering(false);
        }, 500);
    };

    const handleExportToExcel = () => {
        const dataToExport = filteredData.map(item => {
            const row = {};
            selectedColumns.forEach(col => {
                const displayVal = getProgramValue(item, col);
                row[columnRenameMap[col] || col.replace(/_/g, " ")] =
                    isDateColumn(col) ? formatDate(displayVal) : displayVal;


            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "OptOutReport");

        // Generate today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split("T")[0];

        // Create a dynamic filename with the date
        const fileName = `OptOut_Report_${today}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };




    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "99%",
                    height: "95%",
                    bgcolor: "white",
                    boxShadow: 24,
                    p: 0,
                    borderRadius: "0px",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Fixed Header */}
                <Box sx={headerStyle}>
                    <Typography id="run-report-modal" variant="h6">
                        Opt Out Report
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: "white" }}>
                        ✕
                    </IconButton>
                </Box>

                {/* Main Content: Filters + Table */}
                <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>

                    {/* Left-side Filters */}
                    <Box
                        sx={{
                            width: "295px",
                            borderRight: "1px solid #ddd",
                            p: 1,
                            bgcolor: "#f9f9f9",
                            overflowY: "auto",
                        }}
                    >
                        {/* Corporation Filter */}
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>Corporation</Typography>
                        <input
                            type="text"
                            placeholder="Search Corporation..."
                            value={searchCorp}
                            onChange={(e) => setSearchCorp(e.target.value)}
                            style={{ width: "280px", padding: "5px", marginBottom: "5px", borderRadius: "4px", border: "1px solid #ccc" }}
                        />
                        <Box sx={{ maxHeight: "50px", overflowY: "auto",width: "290px", border: "1px solid #ccc", borderRadius: "4px", p: 0, fontSize:"12px" }}>
                            {uniqueCorporations
                                .filter(corp => corp?.toLowerCase().includes(searchCorp.toLowerCase()))
                                .map(corp => (
                                    <div key={corp}>
                                        <input
                                            type="checkbox"
                                            checked={selectedCorporations.includes(corp)}
                                            onChange={() => handleCheckboxChange("corporation", corp)}
                                        />
                                        {corp}
                                    </div>
                                ))}

                        </Box>

                        {/* Facility Filter */}
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>Facility</Typography>
                        <input
                            type="text"
                            placeholder="Search Facility..."
                            value={searchFacility}
                            onChange={(e) => setSearchFacility(e.target.value)}
                            style={{ width: "280px", padding: "5px", marginBottom: "5px", borderRadius: "4px", border: "1px solid #ccc" }}
                        />
                        <Box sx={{maxHeight: "50px", overflowY: "auto",width: "290px", border: "1px solid #ccc", borderRadius: "4px", p: 0, fontSize:"12px" }}>
                            {uniqueFacilities
                                .filter(fac => fac?.toLowerCase().includes(searchFacility.toLowerCase()))
                                .map(fac => (
                                    <div key={fac}>
                                        <input
                                            type="checkbox"
                                            checked={selectedFacilities.includes(fac)}
                                            onChange={() => handleCheckboxChange("facility", fac)}
                                        />
                                        {fac}
                                    </div>
                                ))}

                        </Box>

                        {/* State Filter */}
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>State</Typography>
                        <input
                            type="text"
                            placeholder="Search State..."
                            value={searchState}
                            onChange={(e) => setSearchState(e.target.value)}
                            style={{ width: "280px", padding: "5px", marginBottom: "5px", borderRadius: "4px", border: "1px solid #ccc" }}
                        />
                        <Box sx={{ maxHeight: "50px", overflowY: "auto",width: "290px", border: "1px solid #ccc", borderRadius: "4px", p: 0, fontSize:"12px" }}>
                            {uniqueStates
                                .filter(state => state?.toLowerCase().includes(searchState.toLowerCase()))
                                .map(state => (
                                    <div key={state}>
                                        <input
                                            type="checkbox"
                                            checked={selectedStates.includes(state)}
                                            onChange={() => handleCheckboxChange("state", state)}
                                        />
                                        {state}
                                    </div>
                                ))}

                        </Box>


                        {/* Patient Name Input (Not Functional Yet) */}
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>Patient Name</Typography>
                        <input
                            type="text"
                            placeholder="Search patient..."
                            value={searchPatient}
                            onChange={(e) => setSearchPatient(e.target.value)}
                            disabled={!isPatientSearchEnabled}
                        />

                        <Typography variant="subtitle1" sx={{ mt: 2 }}>
                            Select Columns
                        </Typography>

                        <Box sx={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            maxHeight: "100px",
                            overflowY: "auto",
                            width: "290px",
                            p: 0,
                            backgroundColor: "white",
                        }}>
                            {columnOptions
                                .filter(col => !excludedColumns.includes(col)) // Exclude unwanted columns
                                .map(col => (
                                    <div key={col} style={{ display: "flex", alignItems: "center" }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedColumns.includes(col)}
                                            onChange={() => {
                                                setSelectedColumns(prev =>
                                                    prev.includes(col)
                                                        ? prev.filter(c => c !== col)
                                                        : [...prev, col]
                                                );
                                            }}
                                        />
                                        <span style={{ marginLeft: "5px" }}>
                    {columnRenameMap[col] || col.replace(/_/g, " ")}
                </span>
                                    </div>
                                ))}
                        </Box>

                        <Button
                            variant="contained"
                            sx={{ mt: 1, width: "100%" }}
                            onClick={() => setSelectedColumns([...selectedColumns])}
                        >
                            Update Columns
                        </Button>



                        {(selectedCorporations.length > 0 || selectedFacilities.length > 0 || selectedStates.length > 0) && (
                            <Button
                                variant="contained"
                                sx={{ mt: 10, width: "100%" }}
                                onClick={handleExportToExcel}
                            >
                                Export to Excel
                            </Button>
                        )}



                    </Box>

                    {/* Right-side Table (Scrollable) */}
                    <Box sx={{
                        flex: 1,
                        overflowX: "auto",
                        overflowY: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        px: 2, // Adds padding on the left and right
                        pr: 0 // Adds extra right padding to prevent overlap with the scrollbar
                    }}>


                        {/* Loading Indicator */}
                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <CircularProgress size={50} />
                            </Box>
                        ) : filteredData.length === 0 && (selectedCorporations.length > 0 || selectedFacilities.length > 0 || selectedStates.length > 0) ? (
                            <Typography sx={{ mt: 5, textAlign: "center", fontSize: "18px", fontWeight: "bold" }}>
                                No data available for the selected filters.
                            </Typography>
                        ) : selectedCorporations.length === 0 && selectedFacilities.length === 0 && selectedStates.length === 0 ? (
                            <Typography sx={{ mt: 5, textAlign: "center", fontSize: "18px", fontWeight: "bold" }}>
                                Please select at least one filter to view results.
                            </Typography>
                        ) : filtering ? (
                            // Show a spinner while filtering is happening
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                <CircularProgress size={50} />
                            </Box>
                        ) : filteredData.length === 0 ? (
                            <Typography sx={{ mt: 5, textAlign: "center", fontSize: "18px", fontWeight: "bold" }}>
                                No data available for the selected filters.
                            </Typography>
                        ) : (
                            // Show the table once filtering is complete
                            <Box sx={{ flex: 1, overflowY: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                    <tr>
                                        {columnOrder
                                            .filter(col => selectedColumns.includes(col)) // Keep only selected columns
                                            .map(col => (
                                                <th key={col} style={{ borderBottom: "1px solid black", padding: "5px" }}>
                                                    {columnRenameMap[col] || col.replace(/_/g, " ").toUpperCase()}
                                                </th>
                                            ))}
                                    </tr>
                                    </thead>


                                    <tbody>
                                    {filteredData.map((item, index) => (
                                        <tr key={index}>
                                            {columnOrder
                                                .filter(col => selectedColumns.includes(col)) // Keep only selected columns
                                                .map(col => (
                                                    <td key={col} style={{ borderBottom: "1px solid gray", padding: "5px" }}>
                                                        {isDateColumn(col)
                                                            ? formatDate(getProgramValue(item, col))
                                                            : getProgramValue(item, col)}
                                                    </td>
                                                ))}
                                        </tr>
                                    ))}
                                    </tbody>


                                </table>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Modal>
    );


};

export default OptOutReportModal;